import { toast } from "react-toastify";
import { authErrorCodeName, type AuthErrorCodeName } from "@contracts";

export interface AuthError {
  code: string;
  message: string;
  httpStatus: number;
  isLockout: boolean;
  lockoutSeconds?: number;
}

/**
 * What the screen says for each auth error code, keyed by the code's NAME from the backend contract
 * (`@contracts`, `auth/error-codes.ts`), never by its number: the old table keyed by number and disagreed with the
 * backend on almost every code (P20EE-105 — `AUTH_012` showed "E-mail não verificado" for MFA_REQUIRED). A code the
 * backend adds without an entry here fails the typecheck.
 *
 * `null` shows the server's own message, sanitized: USER_PENDING says whose approval the account waits for ("do
 * hospital" or "interna", by role, P20EE-203), and SESSION_REVOKED carries two different meanings on the backend, so a
 * fixed sentence here would say less than the server.
 */
const AUTH_ERROR_MESSAGES = {
  INVALID_CREDENTIALS: "Credenciais inválidas. Verifique seus dados.",
  INVALID_IDENTIFIER: "CPF ou e-mail inválido. Verifique seus dados.",
  INVALID_CODE: "Código MFA inválido. Tente novamente.",
  INVALID_TOKEN: "Token de acesso inválido. Faça login novamente.",
  EXPIRED_TOKEN: "Sessão expirada. Faça login novamente.",
  INVALID_REFRESH_TOKEN: "Token de refresh inválido. Faça login novamente.",
  EXPIRED_REFRESH_TOKEN: "Sessão expirada. Faça login novamente.",
  USER_NOT_FOUND: "Credenciais inválidas. Verifique seus dados.",
  USER_INACTIVE: "Conta desativada. Entre em contato com o suporte.",
  USER_LOCKED: "Muitas tentativas. Tente novamente em 15 minutos.",
  USER_BLOCKED: "Conta bloqueada. Entre em contato com o suporte.",
  USER_PENDING: null,
  MFA_REQUIRED: "Verificação MFA pendente. Digite os 6 dígitos do código.",
  SESSION_NOT_FOUND: "Sessão não encontrada. Atualize a página e tente novamente.",
  SESSION_REVOKED: null,
  SESSION_COMPROMISED: "Sessão comprometida. Por segurança, todas as sessões foram encerradas.",
  REFRESH_IN_PROGRESS: "Renovação da sessão em andamento. Tente novamente.",
  CODE_EXPIRED: "Código MFA expirado. Solicite um novo código.",
  CODE_ALREADY_USED: "Código MFA já utilizado. Solicite um novo código.",
  CODE_NOT_FOUND: "Código MFA não encontrado. Solicite um novo código.",
  INTERNAL_ERROR: null,
  DATABASE_ERROR: "Erro inesperado. Tente novamente.",
  EXTERNAL_SERVICE_ERROR: "Erro inesperado. Tente novamente.",
} satisfies Record<AuthErrorCodeName, string | null>;

/** The fixed sentence for a backend code, or `null` when the code is unknown or defers to the server's message. */
function messageForCode(code: unknown): string | null {
  const name = authErrorCodeName(code);
  return name ? AUTH_ERROR_MESSAGES[name] : null;
}

const LOCKOUT_DEFAULT_SECONDS = 15 * 60;

const INTERNAL_PATTERNS = [
  /stackTrace/i,
  /at\s+\w+\.\w+/,
  /Error:/i,
  /\/api\/auth/i,
  /\.\w+\(\w+\.ts:\d+/,
  /ENOENT/,
  /ECONNREFUSED/,
  /sequelize/i,
  /prisma/i,
  /database/i,
  /internal/i,
];

export function sanitizeErrorMessage(raw: string): string {
  if (!raw || typeof raw !== "string") return "Erro inesperado. Tente novamente.";

  for (const pattern of INTERNAL_PATTERNS) {
    if (pattern.test(raw)) {
      return "Erro inesperado. Tente novamente.";
    }
  }

  const trimmed = raw.trim();
  if (trimmed.length > 200) {
    return trimmed.slice(0, 200).replace(/\s+$/, "") + "...";
  }

  return trimmed;
}

export function parseAuthError(error: any): AuthError {
  const response = error?.response;
  const status = response?.status || 0;
  const data = response?.data || {};
  const code = data.code || data.error || "";
  const message = data.message || "";

  // Lockout (429 Too Many Requests)
  if (status === 429) {
    const retryAfter = response?.headers?.["retry-after"];
    const lockoutSeconds = retryAfter
      ? parseInt(retryAfter, 10)
      : LOCKOUT_DEFAULT_SECONDS;

    return {
      code: "LOCKOUT",
      message: `Muitas tentativas. Tente novamente em ${Math.ceil(lockoutSeconds / 60)} minutos.`,
      httpStatus: status,
      isLockout: true,
      lockoutSeconds,
    };
  }

  // Mapeia código de erro do backend
  const fixedMessage = messageForCode(code);
  if (fixedMessage) {
    return {
      code,
      message: fixedMessage,
      httpStatus: status,
      isLockout: false,
    };
  }

  // Fallback para mensagem do backend (sanitizada)
  if (message) {
    return {
      code: code || "UNKNOWN",
      message: sanitizeErrorMessage(message),
      httpStatus: status,
      isLockout: false,
    };
  }

  // Erro de rede
  if (!response) {
    return {
      code: "NETWORK_ERROR",
      message: "Falha na conexão com o servidor. Verifique sua internet.",
      httpStatus: 0,
      isLockout: false,
    };
  }

  return {
    code: code || "UNKNOWN",
    message: "Erro inesperado. Tente novamente.",
    httpStatus: status,
    isLockout: false,
  };
}

export function handleAuthError(error: any, options?: { silent?: boolean }): AuthError {
  const authError = parseAuthError(error);

  if (!options?.silent) {
    // toastId estável por código garante que o mesmo erro não empilhe
    // (ex: usuário clica "Entrar" duas vezes seguidas com credenciais erradas).
    toast.error(authError.message, { toastId: `auth-${authError.code}` });
  }

  return authError;
}

export function formatLockoutCountdown(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}
